import React, { useState } from "react";
import { Button, Card, Col, Row, Form, Alert, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import './AjouterClient.css';
import { API_BASE_URL } from "../constants/api";

export default function AjouterClient() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [step, setStep] = useState(1);
    
    // Toast notification
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });
    
    const [formData, setFormData] = useState({
        code_client: '',
        raison_sociale: '',
        type_client: 'entreprise',
        civilite: 'M.',
        nom: '',
        prenom: '',
        adresse: '',
        ville: '',
        code_postal: '',
        pays: 'Gabon',
        telephone: '',
        telephone2: '',
        email: '',
        site_web: '',
        numero_compte: '',
        ice: '',
        patente: '',
        rc: '',
        identifiant_fiscal: '',
        cnss: '',
        forme_juridique: 'SARL',
        plafond_credit: 0,
        solde_initial: 0,
        mode_reglement_defaut: 'especes',
        delai_paiement: 30,
        condition_paiement: 'comptant',
        tarif_applique: 'standard',
        notes: ''
    });

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: "", type: "success" });
        }, 4000);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.raison_sociale && formData.type_client === 'entreprise') {
            showToast("La raison sociale est obligatoire", "error");
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(formData)
                }
            );

            const result = await response.json();

            if (response.ok && result.success) {
                showToast("Client créé avec succès !", "success");
                setTimeout(() => {
                    navigate('/ventes/clients/liste');
                }, 2000);
            } else {
                showToast(result.message || "Erreur lors de la création du client", "error");
            }
        } catch (err) {
            showToast("Erreur de connexion au serveur", "error");
        } finally {
            setLoading(false);
        }
    };

    const nextStep = () => {
        if (step === 1 && !formData.raison_sociale && formData.type_client === 'entreprise') {
            showToast("La raison sociale est obligatoire", "error");
            return;
        }
        setStep(step + 1);
    };

    const prevStep = () => {
        setStep(step - 1);
    };

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                
                {/* Toast Notification */}
                {toast.show && (
                    <div className="toast-notification toast-notification-show">
                        <div className={`toast-content toast-${toast.type}`}>
                            <div className="toast-icon">
                                {toast.type === 'success' ? (
                                    <i className="ri-checkbox-circle-line"></i>
                                ) : (
                                    <i className="ri-error-warning-line"></i>
                                )}
                            </div>
                            <div className="toast-message">
                                <h6>{toast.type === 'success' ? 'Succès' : 'Erreur'}</h6>
                                <p>{toast.message}</p>
                            </div>
                            <button className="toast-close" onClick={() => setToast({ show: false, message: "", type: "success" })}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="toast-progress"></div>
                    </div>
                )}

                {/* En-tête */}
                <div className="d-md-flex align-items-center justify-content-between mb-4">
                    <div>
                        <ol className="breadcrumb fs-sm mb-1">
                            <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                            <li className="breadcrumb-item"><Link to="/ventes">Ventes</Link></li>
                            <li className="breadcrumb-item"><Link to="/ventes/clients/liste">Clients</Link></li>
                            <li className="breadcrumb-item active">Ajouter un client</li>
                        </ol>
                        <h4 className="main-title mb-0 d-flex align-items-center">
                            <i className="ri-add-line me-2" style={{ color: '#21ACCD' }}></i>
                            Nouveau client
                        </h4>
                    </div>
                </div>

                {/* Stepper */}
                <div className="stepper mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                            <div className="step-number">1</div>
                            <div className="step-label">Informations générales</div>
                        </div>
                        <div className="step-line"></div>
                        <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                            <div className="step-number">2</div>
                            <div className="step-label">Identifiants juridiques</div>
                        </div>
                        <div className="step-line"></div>
                        <div className={`step ${step >= 3 ? 'active' : ''}`}>
                            <div className="step-number">3</div>
                            <div className="step-label">Paramètres financiers</div>
                        </div>
                    </div>
                </div>

                <Row className="justify-content-center">
                    <Col xl="10">
                        <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                            {/* Header avec couleur #21ACCD */}
                            <Card.Header className="py-3" style={{ background: '#21ACCD' }}>
                                <div className="d-flex align-items-center">
                                    <div className="client-avatar-large bg-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                         style={{ width: '50px', height: '50px', fontSize: '20px', color: '#21ACCD' }}>
                                        <i className="ri-user-add-line"></i>
                                    </div>
                                    <div>
                                        <h5 className="mb-0 text-white">Création d'un nouveau client</h5>
                                        <p className="mb-0 text-white-50 small">Remplissez toutes les informations nécessaires</p>
                                    </div>
                                </div>
                            </Card.Header>

                            <Card.Body className="p-4">
                                <Form onSubmit={handleSubmit}>
                                    {/* Étape 1: Informations générales */}
                                    {step === 1 && (
                                        <div className="step-content">
                                            <h5 className="mb-4 pb-2 border-bottom">
                                                <i className="ri-information-line me-2" style={{ color: '#21ACCD' }}></i>
                                                Informations générales
                                            </h5>
                                            
                                            <Row className="g-3">
                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Type de client</Form.Label>
                                                        <div className="d-flex gap-4 mt-2">
                                                            <Form.Check
                                                                type="radio"
                                                                label="Entreprise"
                                                                name="type_client"
                                                                value="entreprise"
                                                                checked={formData.type_client === 'entreprise'}
                                                                onChange={handleChange}
                                                                className="p-3 bg-light rounded-3 border"
                                                                style={{ minWidth: '150px', cursor: 'pointer' }}
                                                            />
                                                            <Form.Check
                                                                type="radio"
                                                                label="Particulier"
                                                                name="type_client"
                                                                value="particulier"
                                                                checked={formData.type_client === 'particulier'}
                                                                onChange={handleChange}
                                                                className="p-3 bg-light rounded-3 border"
                                                                style={{ minWidth: '150px', cursor: 'pointer' }}
                                                            />
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Code client <span className="text-muted">(optionnel)</span></Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="code_client"
                                                            value={formData.code_client}
                                                            onChange={handleChange}
                                                            placeholder="Généré automatiquement si vide"
                                                            className="border-2"
                                                        />
                                                        <Form.Text className="text-muted">
                                                            Laissez vide pour génération automatique
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>

                                                {formData.type_client === 'entreprise' ? (
                                                    <>
                                                        <Col md="12">
                                                            <Form.Group>
                                                                <Form.Label>Raison sociale <span className="text-danger">*</span></Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    name="raison_sociale"
                                                                    value={formData.raison_sociale}
                                                                    onChange={handleChange}
                                                                    placeholder="Nom de l'entreprise"
                                                                    required
                                                                    className="border-2"
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md="4">
                                                            <Form.Group>
                                                                <Form.Label>Forme juridique</Form.Label>
                                                                <Form.Select
                                                                    name="forme_juridique"
                                                                    value={formData.forme_juridique}
                                                                    onChange={handleChange}
                                                                    className="border-2"
                                                                >
                                                                    <option value="SARL">SARL</option>
                                                                    <option value="SA">SA</option>
                                                                    <option value="SAS">SAS</option>
                                                                    <option value="EI">Entreprise Individuelle</option>
                                                                    <option value="SNC">SNC</option>
                                                                    <option value="Association">Association</option>
                                                                    <option value="Autre">Autre</option>
                                                                </Form.Select>
                                                            </Form.Group>
                                                        </Col>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Col md="2">
                                                            <Form.Group>
                                                                <Form.Label>Civilité</Form.Label>
                                                                <Form.Select
                                                                    name="civilite"
                                                                    value={formData.civilite}
                                                                    onChange={handleChange}
                                                                    className="border-2"
                                                                >
                                                                    <option value="M.">M.</option>
                                                                    <option value="Mme">Mme</option>
                                                                    <option value="Mlle">Mlle</option>
                                                                </Form.Select>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md="5">
                                                            <Form.Group>
                                                                <Form.Label>Nom</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    name="nom"
                                                                    value={formData.nom}
                                                                    onChange={handleChange}
                                                                    className="border-2"
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md="5">
                                                            <Form.Group>
                                                                <Form.Label>Prénom</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    name="prenom"
                                                                    value={formData.prenom}
                                                                    onChange={handleChange}
                                                                    className="border-2"
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </>
                                                )}

                                                <Col md="12">
                                                    <Form.Group>
                                                        <Form.Label>Adresse</Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            name="adresse"
                                                            value={formData.adresse}
                                                            onChange={handleChange}
                                                            placeholder="Adresse complète"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="4">
                                                    <Form.Group>
                                                        <Form.Label>Ville</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="ville"
                                                            value={formData.ville}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="4">
                                                    <Form.Group>
                                                        <Form.Label>Code postal</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="code_postal"
                                                            value={formData.code_postal}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="4">
                                                    <Form.Group>
                                                        <Form.Label>Pays</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="pays"
                                                            value={formData.pays}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Téléphone</Form.Label>
                                                        <Form.Control
                                                            type="tel"
                                                            name="telephone"
                                                            value={formData.telephone}
                                                            onChange={handleChange}
                                                            placeholder="+241 XX-XXXXXX"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Téléphone secondaire</Form.Label>
                                                        <Form.Control
                                                            type="tel"
                                                            name="telephone2"
                                                            value={formData.telephone2}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Email</Form.Label>
                                                        <Form.Control
                                                            type="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={handleChange}
                                                            placeholder="contact@entreprise.com"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Site web</Form.Label>
                                                        <Form.Control
                                                            type="url"
                                                            name="site_web"
                                                            value={formData.site_web}
                                                            onChange={handleChange}
                                                            placeholder="https://"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>
                                    )}

                                    {/* Étape 2: Identifiants juridiques */}
                                    {step === 2 && (
                                        <div className="step-content">
                                            <h5 className="mb-4 pb-2 border-bottom">
                                                <i className="ri-briefcase-line me-2" style={{ color: '#21ACCD' }}></i>
                                                Identifiants juridiques
                                            </h5>
                                            
                                            <Row className="g-3">
                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Numéro de compte</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="numero_compte"
                                                            value={formData.numero_compte}
                                                            onChange={handleChange}
                                                            placeholder="N° de compte bancaire"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>ICE (Identifiant Commun des Entreprises)</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="ice"
                                                            value={formData.ice}
                                                            onChange={handleChange}
                                                            placeholder="Numéro ICE"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Patente</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="patente"
                                                            value={formData.patente}
                                                            onChange={handleChange}
                                                            placeholder="Numéro de patente"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>RC (Registre de Commerce)</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="rc"
                                                            value={formData.rc}
                                                            onChange={handleChange}
                                                            placeholder="Numéro RC"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Identifiant fiscal (IF)</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="identifiant_fiscal"
                                                            value={formData.identifiant_fiscal}
                                                            onChange={handleChange}
                                                            placeholder="Identifiant fiscal"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>CNSS</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="cnss"
                                                            value={formData.cnss}
                                                            onChange={handleChange}
                                                            placeholder="Numéro CNSS"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <div className="mt-4 p-3 rounded-3" style={{ background: '#f0f9ff' }}>
                                                <i className="ri-information-line me-2" style={{ color: '#21ACCD' }}></i>
                                                <small>
                                                    Ces identifiants sont spécifiques aux entreprises. 
                                                    Laissez vide si non applicable.
                                                </small>
                                            </div>
                                        </div>
                                    )}

                                    {/* Étape 3: Paramètres financiers */}
                                    {step === 3 && (
                                        <div className="step-content">
                                            <h5 className="mb-4 pb-2 border-bottom">
                                                <i className="ri-bank-card-line me-2" style={{ color: '#21ACCD' }}></i>
                                                Paramètres financiers
                                            </h5>
                                            
                                            <Row className="g-3">
                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Plafond de crédit</Form.Label>
                                                        <div className="input-group">
                                                            <span className="input-group-text bg-light border-2">FCFA</span>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.01"
                                                                name="plafond_credit"
                                                                value={formData.plafond_credit}
                                                                onChange={handleChange}
                                                                className="border-2"
                                                            />
                                                        </div>
                                                        <Form.Text className="text-muted">
                                                            0 = pas de crédit autorisé
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Solde initial</Form.Label>
                                                        <div className="input-group">
                                                            <span className="input-group-text bg-light border-2">FCFA</span>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.01"
                                                                name="solde_initial"
                                                                value={formData.solde_initial}
                                                                onChange={handleChange}
                                                                className="border-2"
                                                            />
                                                        </div>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Mode de règlement par défaut</Form.Label>
                                                        <Form.Select
                                                            name="mode_reglement_defaut"
                                                            value={formData.mode_reglement_defaut}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                        >
                                                            <option value="especes">Espèces</option>
                                                            <option value="cheque">Chèque</option>
                                                            <option value="virement">Virement</option>
                                                            <option value="carte_bancaire">Carte bancaire</option>
                                                            <option value="traite">Traite</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Délai de paiement (jours)</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            name="delai_paiement"
                                                            value={formData.delai_paiement}
                                                            onChange={handleChange}
                                                            min="0"
                                                            max="365"
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Condition de paiement</Form.Label>
                                                        <Form.Select
                                                            name="condition_paiement"
                                                            value={formData.condition_paiement}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                        >
                                                            <option value="comptant">Comptant</option>
                                                            <option value="30j">30 jours fin de mois</option>
                                                            <option value="60j">60 jours fin de mois</option>
                                                            <option value="acompte">Acompte + solde</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="6">
                                                    <Form.Group>
                                                        <Form.Label>Tarif appliqué</Form.Label>
                                                        <Form.Select
                                                            name="tarif_applique"
                                                            value={formData.tarif_applique}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                        >
                                                            <option value="standard">Tarif standard</option>
                                                            <option value="grossiste">Tarif grossiste</option>
                                                            <option value="premium">Tarif premium</option>
                                                            <option value="promo">Promotionnel</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col md="12">
                                                    <Form.Group>
                                                        <Form.Label>Notes / Observations</Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            name="notes"
                                                            value={formData.notes}
                                                            onChange={handleChange}
                                                            placeholder="Informations complémentaires..."
                                                            className="border-2"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>
                                    )}

                                    <hr className="my-4" />

                                    {/* Boutons de navigation */}
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            {step > 1 && (
                                                <Button 
                                                    variant="outline-secondary" 
                                                    onClick={prevStep}
                                                    className="px-4 py-2 rounded-3"
                                                >
                                                    <i className="ri-arrow-left-line me-2"></i>
                                                    Étape précédente
                                                </Button>
                                            )}
                                        </div>
                                        <div className="d-flex gap-3">
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/ventes/clients/liste')}
                                                className="px-4 py-2 rounded-3"
                                            >
                                                Annuler
                                            </Button>
                                            {step < 3 ? (
                                                <Button 
                                                    variant="primary" 
                                                    onClick={nextStep}
                                                    className="px-4 py-2 rounded-3"
                                                    style={{ background: '#21ACCD', border: 'none' }}
                                                >
                                                    Étape suivante
                                                    <i className="ri-arrow-right-line ms-2"></i>
                                                </Button>
                                            ) : (
                                                <Button 
                                                    variant="success" 
                                                    type="submit"
                                                    disabled={loading}
                                                    className="px-4 py-2 rounded-3"
                                                    style={{ background: '#21ACCD', border: 'none' }}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" />
                                                            Création en cours...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="ri-check-line me-2"></i>
                                                            Créer le client
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Footer />
            </div>

            {/* Styles */}
            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                .input-group-text {
                    border-width: 2px;
                    border-color: #e2e8f0;
                }
                .client-avatar-large {
                    border: 3px solid rgba(255,255,255,0.3);
                    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
                }
                .text-white-50 {
                    color: rgba(255,255,255,0.7);
                }
                
                /* Stepper */
                .stepper {
                    background: white;
                    padding: 20px 30px;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                }
                .step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                    z-index: 2;
                }
                .step-number {
                    width: 40px;
                    height: 40px;
                    background: #e2e8f0;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    color: #64748b;
                    transition: all 0.3s;
                }
                .step.active .step-number {
                    background: #21ACCD;
                    color: white;
                    box-shadow: 0 0 0 4px rgba(33, 172, 205, 0.2);
                }
                .step.completed .step-number {
                    background: #10b981;
                    color: white;
                }
                .step-label {
                    font-size: 0.75rem;
                    margin-top: 8px;
                    color: #64748b;
                    font-weight: 500;
                }
                .step.active .step-label {
                    color: #21ACCD;
                    font-weight: 600;
                }
                .step-line {
                    flex: 1;
                    height: 2px;
                    background: #e2e8f0;
                    margin: 0 10px;
                }
                
                /* Toast Notifications */
                .toast-notification {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 9999;
                    min-width: 350px;
                    max-width: 450px;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
                    overflow: hidden;
                    transform: translateX(120%);
                    transition: transform 0.3s ease-out;
                    opacity: 0;
                }
                .toast-notification-show {
                    transform: translateX(0);
                    opacity: 1;
                }
                .toast-content {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    padding: 16px 20px;
                    position: relative;
                }
                .toast-success { border-left: 4px solid #10b981; }
                .toast-error { border-left: 4px solid #ef4444; }
                .toast-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                }
                .toast-success .toast-icon { background: #ecfdf5; color: #10b981; }
                .toast-error .toast-icon { background: #fef2f2; color: #ef4444; }
                .toast-message { flex: 1; }
                .toast-message h6 { margin: 0 0 4px 0; font-size: 0.9rem; font-weight: 600; }
                .toast-message p { margin: 0; font-size: 0.8rem; color: #64748b; }
                .toast-close {
                    background: transparent;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: color 0.2s;
                }
                .toast-close:hover { color: #1e293b; }
                .toast-progress {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    height: 3px;
                    width: 100%;
                    background: rgba(0,0,0,0.05);
                    animation: progress 4s linear forwards;
                }
                .toast-success .toast-progress { background: linear-gradient(90deg, #10b981, #059669); }
                .toast-error .toast-progress { background: linear-gradient(90deg, #ef4444, #dc2626); }
                @keyframes progress {
                    0% { width: 100%; }
                    100% { width: 0%; }
                }
                @media (max-width: 768px) {
                    .toast-notification {
                        top: 10px;
                        right: 10px;
                        left: 10px;
                        min-width: auto;
                        max-width: none;
                    }
                    .stepper .step-label {
                        font-size: 0.6rem;
                    }
                    .step-number {
                        width: 32px;
                        height: 32px;
                        font-size: 12px;
                    }
                }
            `}</style>
        </React.Fragment>
    );
}